import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App.tsx'
import './index.css'

// ── Google Translate safeguard (client crash report, both environments) ──────
// Chrome's auto-translate wraps text nodes in <font> tags, moving them out of
// the positions React tracks. React's next update then throws
// "NotFoundError: Failed to execute 'insertBefore'/'removeChild' on 'Node'"
// and the error boundary takes over mid-demo. Known React issue (#11538);
// this is the standard safeguard: when the child was relocated by an outside
// actor, degrade gracefully instead of crashing. Translation keeps working.
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (console) {
        console.warn('[translate-safeguard] removeChild on detached node ignored (page likely machine-translated)')
      }
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console) {
        console.warn('[translate-safeguard] insertBefore with detached reference — appending instead (page likely machine-translated)')
      }
      return this.appendChild(newNode) as T
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
)
